/**
 * ExecutiveStandfirst — page-top editorial block for the Executive Report.
 *
 * Mirrors `Standfirst` (Command Center) and `PillarBrief` (Posture pages) in
 * shape: kicker pill → italic-mixed headline → deck → byline → ScoreBox.
 * The single difference is the voice: the headline modifier reads against a
 * 30/60/90-day reporting period (not "today"), and the byline names the
 * tenant + period rather than the signed-in user.
 *
 * We don't reuse PillarBrief directly because the Executive Report is not a
 * pillar (no `data-tone="compliance|bpa|identity"` attached to the section).
 * Instead we render with the primary brand accent (Guardian green) the same
 * way the Command Center does.
 */

import type { ExecutiveReportBrief } from '@/lib/executive-report-brief'
import ScoreBox from '@/components/Dashboard/v2/ScoreBox'

interface ScoreComponent {
  label: string
  weight: string
  value: number
  fillClass: string
}

interface Props {
  brief: ExecutiveReportBrief
  /** Composite Guardian Score (0-100). */
  score: number | null
  /** Component breakdown for the score box. */
  components: ScoreComponent[]
  isLoading?: boolean
}

/** Render a string with `**bold**` markers as <strong>. Same shorthand the
 *  brief composers produce — kept local so we don't import the renderer
 *  from another component file. */
function renderEmphasis(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-bold text-current">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return <span key={i}>{part}</span>
  })
}

export default function ExecutiveStandfirst({
  brief,
  score,
  components,
  isLoading,
}: Props) {
  return (
    <section
      aria-labelledby="exec-standfirst-headline"
      className="
        standfirst-rule
        grid grid-cols-1 gap-10 pb-10 pt-2
        lg:grid-cols-[1.5fr_1fr] lg:gap-14
      "
    >
      <div className="lg:pr-6">
        <span
          className="
            inline-flex items-center gap-2.5 rounded-[2px] bg-[#212938] px-3.5 py-1.5
            text-[10px] font-bold uppercase tracking-[0.28em] text-white
          "
        >
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full"
            style={{
              background: 'rgb(var(--color-primary-500))',
              boxShadow: '0 0 6px rgb(var(--color-primary-500))',
            }}
          />
          {brief.kicker}
        </span>

        <h1
          id="exec-standfirst-headline"
          className="mt-5 font-display font-extrabold tracking-[-0.025em]"
          style={{
            fontSize: 'clamp(36px, 5vw, 60px)',
            lineHeight: 0.96,
            color: 'rgb(var(--color-text-primary))',
          }}
        >
          {brief.headlineSubject}{' '}
          <em
            style={{
              fontStyle: 'italic',
              fontWeight: 600,
              color: 'rgb(var(--color-primary-700))',
            }}
          >
            {brief.headlineModifier}
          </em>
          .
          <br />
          {brief.weakLink}
        </h1>

        <p
          className="mt-5 max-w-xl text-[16px] leading-[1.55]"
          style={{ color: 'rgb(var(--color-text-secondary))' }}
        >
          {renderEmphasis(brief.deck)}
        </p>

        <div
          className="
            mt-6 flex flex-wrap gap-4 border-t pt-3.5
            text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500
          "
          style={{ borderTopColor: 'rgb(var(--color-dark-600))' }}
        >
          {brief.byline.split(' · ').map((segment, i, arr) => (
            <span key={i} className="contents">
              <span>{renderEmphasis(segment)}</span>
              {i < arr.length - 1 && <span aria-hidden>·</span>}
            </span>
          ))}
        </div>
      </div>

      <ScoreBox
        score={score}
        verdict={brief.verdict}
        delta={brief.delta}
        components={components}
        isLoading={isLoading}
      />
    </section>
  )
}
